// Standalone payment-authorisation ledger — independent of the FIDIC contract
// generator. A Project holds Contracts (prime + subcontracts + supply +
// consultants), and each Contract holds Payment Certificates that authorise and
// document a payment (construction progress or material/goods supply against a
// supplier invoice), with the payee's acknowledgement of full settlement to date.

import type { StoredFile } from "../contracts/types";

export type ContractKind = "prime" | "subcontract" | "supply" | "consultant" | "other";
export type CertKind = "construction" | "material-supply" | "services" | "other";
export type CertStatus = "draft" | "authorised" | "acknowledged" | "paid";

export const CONTRACT_KIND_LABEL: Record<ContractKind, string> = {
  prime: "Prime contract",
  subcontract: "Subcontract",
  supply: "Supply",
  consultant: "Consultant",
  other: "Other",
};
export const CERT_KIND_LABEL: Record<CertKind, string> = {
  construction: "Construction / works",
  "material-supply": "Material / goods supply",
  services: "Services",
  other: "Other",
};
export const CERT_STATUS_LABEL: Record<CertStatus, string> = {
  draft: "Draft",
  authorised: "Authorised",
  acknowledged: "Acknowledged",
  paid: "Paid",
};

export interface PaymentCert {
  id: string;
  number: string;
  date: string;
  kind: CertKind;
  description: string;
  /** Supplier invoice reference (material-supply). */
  invoiceRef?: string;
  invoiceDate?: string;
  amount: number;
  currency: string;
  /** Amounts expressly reserved / not settled by this certificate (optional). */
  reserved?: string;
  notes?: string;
  status: CertStatus;
  /** Whether the payee acknowledges this settles all outstanding to date. */
  coversAllOutstanding: boolean;
  acknowledgedBy?: string;
  acknowledgedDate?: string;
  /** Digital approval: the approver's signature image (data URI), name and date,
   * applied when the certificate is approved & signed. */
  authorisedSignature?: string;
  authorisedBy?: string;
  authorisedDate?: string;
  /** Part-payments received against this certificate. The payment state (open /
   * partially paid / paid) is DERIVED from these — never stored — so it can never
   * disagree with the money actually logged. Distinct from `status`, which is the
   * certification workflow (draft → authorised → acknowledged). */
  payments?: CertPayment[];
  /** Id of the live share link for this certificate, once one is created. Stored
   * on the cert (not loose state) so re-sharing reuses the same link and it
   * persists with the ledger + travels with cloud sync. */
  shareId?: string;
  /** Set when a signed certificate has been sent to the payee to confirm receipt
   * of the payment (via the share link). */
  sentForReceipt?: boolean;
  /** The payee's confirmation of receipt, returned from the share link. */
  receipt?: CertReceipt;
  /** FIDIC Clause 14 build-up (valuation, retention, advance recovery, 14.2
   * particulars). Optional — see CertBuildup. */
  buildup?: CertBuildup;
  /** The contractor's / supplier's invoice or payment statement for this
   * certificate (PDF or image in the file store). Attached by the issuer, taken
   * from the scanned document, or uploaded by the contractor on the share link.
   * It travels with the certificate's share link so the client gets both. */
  invoice?: StoredFile;
  /** Emails sent from the app with this certificate (to whom, when, with what),
   * so the ledger holds the record of what went to the client. */
  emailLog?: EmailRecord[];
  createdAt?: string;
}

/** One email the app sent about a certificate. */
export interface EmailRecord {
  id?: string;
  to: string[];
  cc?: string[];
  subject: string;
  at: string;
  attachments?: string[];
}

/** The payee's acknowledgement that they received the certified payment, captured
 * on the share link: their signature, name, date, and a photo of the receipt. */
export interface CertReceipt {
  /** Name of the person confirming receipt for the payee. */
  confirmedBy?: string;
  /** Their signature (drawn on the share link), as a PNG data URI. */
  signature?: string;
  /** Date they confirmed receipt. */
  confirmedDate?: string;
  /** A photo/scan of the receipt they issued for the payment. */
  receiptPhoto?: StoredFile;
  note?: string;
  /** ISO timestamp the confirmation was submitted. */
  at?: string;
}

/** One payment received against a certificate. */
export interface CertPayment {
  id: string;
  amount: number;
  date: string;
  /** Method / reference, e.g. "Bank transfer — ref 88213". */
  ref?: string;
  /** Evidence this payment was made — a bank transfer receipt or the payee's
   * signed acknowledgement. Attached to the payment rather than the certificate
   * so each receipt sits with the money it evidences. */
  proof?: StoredFile;
}

export type PayState = "open" | "partial" | "paid";
export const PAY_STATE_LABEL: Record<PayState, string> = {
  open: "Open",
  partial: "Partially paid",
  paid: "Paid",
};

/** Total actually received against a certificate. */
export function certPaid(c: PaymentCert): number {
  return (c.payments || []).reduce((s, p) => s + (Number.isFinite(p.amount) ? p.amount : 0), 0);
}
/** Outstanding on a certificate (never negative). */
export function certOutstanding(c: PaymentCert): number {
  const amt = Number.isFinite(c.amount) ? c.amount : 0;
  return Math.max(0, amt - certPaid(c));
}
/** Derived payment state. Tolerates tiny rounding (settles within 0.5). */
export function certPayState(c: PaymentCert): PayState {
  const amt = Number.isFinite(c.amount) ? c.amount : 0;
  const paid = certPaid(c);
  if (paid <= 0.005) return "open";
  if (paid >= amt - 0.5) return "paid";
  return "partial";
}

export interface Contract {
  id: string;
  name: string;
  kind: ContractKind;
  /** Contractor / supplier / consultant legal name (the payee). */
  party: string;
  /** Supplier name for tracking (supply contracts); defaults to party. */
  supplier?: string;
  reference?: string;
  value?: number;
  /** Retention % applied to certified value (for the position dashboard). */
  retentionPercent?: number;
  /** The payee's email — prefilled when a certificate is emailed. */
  email?: string;
  currency: string;
  certificates: PaymentCert[];
}

export interface PaymentProject {
  id: string;
  name: string;
  reference?: string;
  location?: string;
  currency: string;
  /** Person/role authorising payments for the project (Employer's rep). */
  authoriser?: string;
  authoriserTitle?: string;
  /** The client's / Employer's email(s) — prefilled when a certificate is emailed. */
  clientEmail?: string;
  /** Soft link to a contract created in the Contract section. */
  linkedContract?: { name: string; reference?: string };
  /** The Atelier project this ledger belongs to. When set, every certificate is
   * mirrored to Atelier (studio cockpit + client portal) as it is saved,
   * shared or emailed. */
  atelierProjectId?: string;
  atelierProjectName?: string;
  /** Overall project budget (in the project currency) to track certificates against. */
  budget?: number;
  /** How foreign-currency amounts are converted to the project currency:
   *  "prevailing" = live market rates (default); "static" = fixed rates set below. */
  rateMode?: "prevailing" | "static";
  /** Static conversion rates, used when rateMode === "static". Keyed by the foreign
   *  currency code; value = how many PROJECT-currency units equal 1 unit of that
   *  currency (e.g. project GYD, { USD: 209 } → 1 USD = 209 GYD). */
  staticRates?: Record<string, number>;
  contracts: Contract[];
  createdAt?: string;
}

/** Seed for creating a payment project from the Contract section's brief. */
export interface ContractSeed {
  projectName: string;
  reference?: string;
  location?: string;
  currency: string;
  employer?: string;
  contractor?: string;
  contractValue?: number;
  atelierProjectId?: string;
}

export interface PaymentsData {
  projects: PaymentProject[];
  /** The user's approval signature (image data URI), stored with the ledger so it
   * persists, exports with the JSON, and travels with cloud sync. */
  signature?: string;
}

// --- Aggregation helpers -----------------------------------------------------
/** An advance/mobilisation payment (FIDIC 14.2) — a repayable loan, NOT certified
 * works value. It does not consume the Contract Price and carries no retention. */
export function isAdvanceCert(c: PaymentCert): boolean {
  return c.kind === "construction" && /\badvance\b/i.test(c.description || "");
}
/** Total of ALL certificates (money authorised out the door, incl. advances). */
export function contractTotal(c: Contract): number {
  return c.certificates.reduce((s, x) => s + (isFinite(x.amount) ? x.amount : 0), 0);
}
/** Value certified for works/goods/services — EXCLUDES repayable advances. This
 * is what counts against the Contract value and what retention is applied to. */
export function worksCertified(c: Contract): number {
  return c.certificates.reduce((s, x) => s + (!isAdvanceCert(x) && isFinite(x.amount) ? x.amount : 0), 0);
}
/** Advance payments disbursed to date under the Contract (repayable). */
export function advancePaid(c: Contract): number {
  return c.certificates.reduce((s, x) => s + (isAdvanceCert(x) && isFinite(x.amount) ? x.amount : 0), 0);
}
export function projectTotal(p: PaymentProject): number {
  return p.contracts.reduce((s, c) => s + contractTotal(c), 0);
}
export interface ConvertedTotal { total: number; unconverted: number; hasForeign: boolean; }
/** Total certified across the project, each certificate converted to `target`
 * via `conv` (returns null if a rate is missing → that amount is left aside). */
export function projectCertifiedIn(p: PaymentProject, target: string, conv: (amount: number, from: string) => number | null): ConvertedTotal {
  let total = 0, unconverted = 0, hasForeign = false;
  for (const c of p.contracts) for (const cert of c.certificates) {
    const amt = isFinite(cert.amount) ? cert.amount : 0;
    const ccy = cert.currency || c.currency || p.currency;
    if (ccy === target) { total += amt; continue; }
    hasForeign = true;
    const v = conv(amt, ccy);
    if (v == null) unconverted += amt; else total += v;
  }
  return { total, unconverted, hasForeign };
}
/** Total actually PAID across the project (sum of payments logged against every
 * certificate), each converted to `target` — the cash-out counterpart to
 * projectCertifiedIn. */
export function projectPaidIn(p: PaymentProject, target: string, conv: (amount: number, from: string) => number | null): ConvertedTotal {
  let total = 0, unconverted = 0, hasForeign = false;
  for (const c of p.contracts) for (const cert of c.certificates) {
    const paid = certPaid(cert);
    if (paid <= 0.005) continue;
    const ccy = cert.currency || c.currency || p.currency;
    if (ccy === target) { total += paid; continue; }
    hasForeign = true;
    const v = conv(paid, ccy);
    if (v == null) unconverted += paid; else total += v;
  }
  return { total, unconverted, hasForeign };
}
/** A contract's total converted to `target` (null if any rate is missing). */
export function contractTotalIn(c: Contract, target: string, projCcy: string, conv: (amount: number, from: string) => number | null): number | null {
  let total = 0;
  for (const cert of c.certificates) {
    const amt = isFinite(cert.amount) ? cert.amount : 0;
    const ccy = cert.currency || c.currency || projCcy;
    if (ccy === target) { total += amt; continue; }
    const v = conv(amt, ccy);
    if (v == null) return null;
    total += v;
  }
  return total;
}
export function projectCertCount(p: PaymentProject): number {
  return p.contracts.reduce((s, c) => s + c.certificates.length, 0);
}

export interface ContractPosition {
  value: number | null;
  certified: number; // works/goods certified (EXCLUDES repayable advances)
  pct: number | null; // % of value certified
  retentionHeld: number; // retention on works only
  remaining: number | null; // value − works certified (advance does NOT reduce this)
  advancePaid: number; // repayable advance disbursed to date (tracked separately)
}
export function contractPosition(c: Contract): ContractPosition {
  const value = c.value != null && isFinite(c.value) ? c.value : null;
  const certified = worksCertified(c); // advances excluded — they are repayable
  const pct = value ? (certified / value) * 100 : null;
  const retentionHeld = ((c.retentionPercent || 0) / 100) * certified; // no retention on advances
  const remaining = value != null ? value - certified : null; // advance does not consume the value
  return { value, certified, pct, retentionHeld, remaining, advancePaid: advancePaid(c) };
}

export interface SupplierRollup {
  supplier: string;
  total: number;
  certs: number;
  contracts: number;
}
export function bySupplier(p: PaymentProject): SupplierRollup[] {
  const map = new Map<string, SupplierRollup>();
  for (const c of p.contracts) {
    const key = (c.supplier || c.party || "—").trim() || "—";
    const r = map.get(key) || { supplier: key, total: 0, certs: 0, contracts: 0 };
    r.total += contractTotal(c);
    r.certs += c.certificates.length;
    r.contracts += 1;
    map.set(key, r);
  }
  return [...map.values()].sort((a, b) => b.total - a.total);
}

// --- Certificate series (derived — nothing new is stored) ---------------------
// A certificate's "series" is read from the fields it already has (kind + the
// wording of its description / number), the same rule the certificate document
// uses to pick its title and FIDIC basis. So an advance is still "a construction
// certificate whose description says advance" — existing ledgers need no change.
export type CertSeries = "advance" | "interim" | "final" | "supply" | "services" | "other";
export const CERT_SERIES_LABEL: Record<CertSeries, string> = {
  advance: "Advance payment (14.2)",
  interim: "Interim payment (IPC)",
  final: "Final payment",
  supply: "Supply invoice",
  services: "Services / fees",
  other: "Other",
};
export const CERT_SERIES_HINT: Record<CertSeries, string> = {
  advance: "Repayable mobilisation advance — no retention, not counted against the contract value, recovered from later IPCs.",
  interim: "Certifies works executed to date under Sub-Clause 14.6. Counts against the contract value; retention applies.",
  final: "The last certificate under the contract — the balance of the final account.",
  supply: "Pays a supplier's invoice for materials or goods.",
  services: "Pays a consultant's or service provider's fee.",
  other: "Any other authorised payment.",
};
/** Which series a contract's certificates naturally belong to. */
export function seriesForContract(c: Contract): CertSeries[] {
  if (c.kind === "supply") return ["supply", "other"];
  if (c.kind === "consultant") return ["services", "other"];
  if (c.kind === "other") return ["other", "supply", "services"];
  return ["advance", "interim", "final", "other"];
}
export function certSeries(c: PaymentCert): CertSeries {
  if (c.kind === "material-supply") return "supply";
  if (c.kind === "services") return "services";
  if (c.kind === "other") return "other";
  if (isAdvanceCert(c)) return "advance";
  if (/\bfinal\b/i.test(c.description || "") || /\bfinal\b/i.test(c.number || "")) return "final";
  return "interim";
}
/** Largest n among numbers matching `re` (capture group 1), else 0. */
function maxSeq(certs: PaymentCert[], re: RegExp): number {
  let max = 0;
  for (const c of certs) {
    const m = re.exec((c.number || "").trim());
    if (m) max = Math.max(max, parseInt(m[1], 10) || 0);
  }
  return max;
}
/** The next certificate number in a series, following the numbering already
 * used on this contract: advances run "IPC.A.1, IPC.A.2…", interims "IPC 1,
 * IPC 2…" (a final certificate continues that run), everything else a plain
 * zero-padded sequence. */
export function nextCertNumber(contract: Contract, series: CertSeries): string {
  const certs = contract.certificates;
  if (series === "advance") {
    const n = Math.max(maxSeq(certs, /^IPC\.A\.(\d+)/i), certs.filter(isAdvanceCert).length);
    return `IPC.A.${n + 1}`;
  }
  if (series === "interim" || series === "final") {
    const n = Math.max(maxSeq(certs, /^IPC\s*(\d+)$/i), certs.filter((c) => certSeries(c) === "interim" || certSeries(c) === "final").length);
    return `IPC ${n + 1}`;
  }
  return `${certs.length + 1}`.padStart(3, "0");
}
/** The ordinal of this certificate within its series (1-based), counting the
 * contract's existing certificates of the same series. */
export function seriesOrdinal(contract: Contract, series: CertSeries, excludeId?: string): number {
  return contract.certificates.filter((c) => c.id !== excludeId && certSeries(c) === series).length + 1;
}
/** Default wording for a new certificate — written so the document builder and
 * the ledger aggregates classify it correctly (an advance must say "advance"). */
export function seriesDescription(series: CertSeries, ordinal: number, date: string): string {
  switch (series) {
    case "advance":
      return ordinal === 1
        ? "First advance disbursement under Sub-Clause 14.2 [Advance Payment], certified against the Advance Payment Guarantee (Sub-Clause 14.2.1)"
        : `Advance disbursement ${ordinal} under Sub-Clause 14.2 [Advance Payment]`;
    case "interim":
      return `Works executed and valued to ${date || "the end of the period"}`;
    case "final":
      return "Final payment — balance of the final account on completion of the Works";
    default:
      return "";
  }
}
/** Rewrite a draft certificate for a series: kind, number and default wording.
 * Used only on NEW drafts, so an existing certificate's number and text are never
 * changed behind the user's back. */
export function applySeries(draft: PaymentCert, series: CertSeries, contract: Contract): PaymentCert {
  const kind: CertKind = series === "supply" ? "material-supply" : series === "services" ? "services" : series === "other" ? "other" : "construction";
  const ordinal = seriesOrdinal(contract, series, draft.id);
  return {
    ...draft,
    kind,
    number: nextCertNumber(contract, series),
    description: seriesDescription(series, ordinal, draft.date),
    invoiceRef: series === "supply" ? draft.invoiceRef || "" : undefined,
    // An advance is a loan — it does not settle anything outstanding for works.
    coversAllOutstanding: series !== "advance",
  };
}

// --- FIDIC Clause 14 build-up stored with a certificate ----------------------
// Optional: a certificate without it is just an amount (as before). With it,
// an interim/final certificate carries the Contractor's Statement valuation
// (14.3, cumulative to date), retention, advance recovery and previous
// certificates, and the amount due is COMPUTED from them. An advance carries
// its 14.2 particulars (percentage, installment, guarantee).
export interface CertBuildup {
  statementRef?: string;
  statementDate?: string;
  periodFrom?: string;
  periodTo?: string;
  engineerName?: string;
  paymentDueDays?: number;
  // Interim / final — valuation of the Works to date (14.3), cumulative
  worksExecuted?: number;
  materialsOnSite?: number;
  variations?: number;
  provisionalSums?: number;
  dayworks?: number;
  priceAdjustment?: number;
  otherAdditions?: number;
  retentionPercent?: number;
  retentionLimitPercent?: number;
  retentionReleased?: number;
  advanceRecoveredToDate?: number;
  /** Amounts certified in previous certificates (14.3(g)). Blank = taken from
   * the ledger automatically (the net due of every earlier interim/final). */
  previouslyCertified?: number;
  vatPercent?: number;
  // Advance (14.2)
  advancePercent?: number;
  installmentIndex?: number;
  installmentCount?: number;
  guaranteeRef?: string;
}
export interface BuildupSummary {
  gross: number;
  retentionGross: number; // before any release
  retentionHeld: number;
  advanceRecovered: number;
  netToDate: number;
  previouslyCertified: number;
  previouslyAuto: boolean; // previouslyCertified came from the ledger, not typed
  dueThisCert: number;
  vat: number;
  totalPayable: number;
}
const n0 = (v?: number) => (v == null || !isFinite(v) ? 0 : v);
/** True when the certificate carries a 14.3 valuation (so its amount is derived). */
export function hasValuation(b?: CertBuildup): boolean {
  if (!b) return false;
  return [b.worksExecuted, b.materialsOnSite, b.variations, b.provisionalSums, b.dayworks, b.priceAdjustment, b.otherAdditions].some((x) => x != null && isFinite(x));
}
/** The contract's interim/final certificates that come BEFORE `cert` (a new,
 * not-yet-saved certificate comes after everything). */
export function earlierWorksCerts(contract: Contract, cert: PaymentCert): PaymentCert[] {
  const idx = contract.certificates.findIndex((c) => c.id === cert.id);
  const before = idx >= 0 ? contract.certificates.slice(0, idx) : contract.certificates;
  return before.filter((c) => { const s = certSeries(c); return s === "interim" || s === "final"; });
}
/** Net amount (excl. VAT) a works certificate put through for payment — the
 * figure that becomes "previously certified" on the next one. */
export function certNetDue(contract: Contract, cert: PaymentCert): number {
  if (hasValuation(cert.buildup)) return computeBuildup(contract, cert).dueThisCert;
  const amt = n0(cert.amount);
  const vat = cert.buildup?.vatPercent;
  return vat ? amt / (1 + vat / 100) : amt;
}
/** Sum of the net due of every earlier interim/final certificate on the contract. */
export function previouslyCertifiedAuto(contract: Contract, cert: PaymentCert): number {
  return earlierWorksCerts(contract, cert).reduce((s, c) => s + certNetDue(contract, c), 0);
}
/** The FIDIC 14 payment build-up for an interim/final certificate. */
export function computeBuildup(contract: Contract, cert: PaymentCert): BuildupSummary {
  const b = cert.buildup || {};
  const gross = n0(b.worksExecuted) + n0(b.materialsOnSite) + n0(b.variations) + n0(b.provisionalSums) + n0(b.dayworks) + n0(b.priceAdjustment) + n0(b.otherAdditions);
  const value = contract.value != null && isFinite(contract.value) ? contract.value : 0;
  const retPct = b.retentionPercent ?? contract.retentionPercent ?? 0;
  let retentionGross = (retPct / 100) * gross;
  if (b.retentionLimitPercent != null && isFinite(b.retentionLimitPercent) && value > 0) retentionGross = Math.min(retentionGross, (b.retentionLimitPercent / 100) * value);
  const retentionHeld = Math.max(0, retentionGross - n0(b.retentionReleased));
  const advanceRecovered = n0(b.advanceRecoveredToDate);
  const netToDate = gross - retentionHeld - advanceRecovered;
  const previouslyAuto = b.previouslyCertified == null || !isFinite(b.previouslyCertified);
  const previouslyCertified = previouslyAuto ? previouslyCertifiedAuto(contract, cert) : (b.previouslyCertified as number);
  const dueThisCert = netToDate - previouslyCertified;
  const vat = b.vatPercent ? (b.vatPercent / 100) * dueThisCert : 0;
  return { gross, retentionGross, retentionHeld, advanceRecovered, netToDate, previouslyCertified, previouslyAuto, dueThisCert, vat, totalPayable: dueThisCert + vat };
}
